import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, Copy, Archive, ArchiveRestore, Trash2, AlertTriangle, X, Share2 } from 'lucide-react';
import { toast } from 'sonner';

/** Menu d'actions partagé (Musculation + Cardio) : dupliquer, archiver/désarchiver, supprimer
 * définitivement (avec confirmation + choix d'archiver à la place pour ne pas perdre le tracking). */
export const ProgramCardMenu = ({ en, isArchived, onDuplicate, onToggleArchive, onDelete, onShare, testIdPrefix }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const stop = (e) => e.stopPropagation();

  const runShare = async (e) => {
    stop(e); setMenuOpen(false);
    try { await onShare(); }
    catch { toast.error(L('Impossible de partager ce programme.', 'Could not share this program.')); }
  };

  const runDuplicate = async (e) => {
    stop(e); setMenuOpen(false); setBusy(true);
    try { await onDuplicate(); toast.success(L('Programme dupliqué !', 'Program duplicated!')); }
    catch { toast.error(L('Erreur lors de la duplication.', 'Error duplicating the program.')); }
    finally { setBusy(false); }
  };

  const runArchiveToggle = async (e) => {
    stop(e); setMenuOpen(false); setBusy(true);
    try {
      await onToggleArchive();
      toast.success(isArchived ? L('Programme restauré !', 'Program restored!') : L('Programme archivé.', 'Program archived.'));
    } catch { toast.error(L('Erreur.', 'Error.')); }
    finally { setBusy(false); }
  };

  const runDelete = async () => {
    setBusy(true);
    try { await onDelete(); toast.success(L('Programme supprimé — récupérable pendant 24h dans la corbeille.', 'Program deleted — recoverable for 24h in the trash.')); setConfirmOpen(false); }
    catch { toast.error(L('Erreur lors de la suppression.', 'Error deleting the program.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative" data-testid={`${testIdPrefix}-menu`}>
      <button onClick={(e) => { stop(e); setMenuOpen((v) => !v); }} disabled={busy}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
        data-testid={`${testIdPrefix}-menu-btn`}>
        <MoreVertical className="w-4 h-4" />
      </button>

      {createPortal(
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm" onClick={(e) => { stop(e); setMenuOpen(false); }} />
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 320 }}
                className="fixed inset-x-0 bottom-0 z-[91] bg-zinc-900 border-t border-white/10 rounded-t-2xl overflow-hidden pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-w-sm sm:left-1/2 sm:-translate-x-1/2 sm:rounded-2xl sm:bottom-6"
                onClick={stop}>
                <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mt-3 mb-1 sm:hidden" />
                <button onClick={runDuplicate} className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-zinc-200 hover:bg-white/5 transition-colors" data-testid={`${testIdPrefix}-duplicate-btn`}>
                  <Copy className="w-4 h-4 text-zinc-400" /> {L('Dupliquer', 'Duplicate')}
                </button>
                {onShare && (
                  <button onClick={runShare} className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-zinc-200 hover:bg-white/5 transition-colors" data-testid={`${testIdPrefix}-share-btn`}>
                    <Share2 className="w-4 h-4 text-zinc-400" /> {L('Partager', 'Share')}
                  </button>
                )}
                <button onClick={runArchiveToggle} className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-zinc-200 hover:bg-white/5 transition-colors" data-testid={`${testIdPrefix}-archive-btn`}>
                  {isArchived ? <ArchiveRestore className="w-4 h-4 text-zinc-400" /> : <Archive className="w-4 h-4 text-zinc-400" />}
                  {isArchived ? L('Restaurer', 'Restore') : L('Archiver', 'Archive')}
                </button>
                <button onClick={(e) => { stop(e); setMenuOpen(false); setConfirmOpen(true); }} className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`${testIdPrefix}-delete-btn`}>
                  <Trash2 className="w-4 h-4" /> {L('Supprimer', 'Delete')}
                </button>
                <button onClick={(e) => { stop(e); setMenuOpen(false); }} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-zinc-400 border-t border-white/10 hover:bg-white/5 transition-colors" data-testid={`${testIdPrefix}-menu-cancel-btn`}>
                  {L('Annuler', 'Cancel')}
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {createPortal(
        <AnimatePresence>
          {confirmOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={(e) => { stop(e); setConfirmOpen(false); }}
              data-testid={`${testIdPrefix}-confirm-modal`}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={stop}
                className="bg-zinc-900 border border-red-500/30 rounded-2xl w-full max-w-sm p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <button onClick={() => setConfirmOpen(false)} data-testid={`${testIdPrefix}-confirm-close`}><X className="w-4 h-4 text-zinc-500" /></button>
                </div>
                <div>
                  <h3 className="text-white font-bold text-base mb-1.5">{L('Supprimer ce programme ?', 'Delete this program?')}</h3>
                  <p className="text-zinc-400 text-sm">
                    {L("Le programme et son historique de tracking (séances, records, progression) seront masqués immédiatement. Vous pourrez le restaurer pendant 24h depuis la corbeille, après quoi la suppression sera définitive.", 'The program and its tracking history (sessions, records, progress) will be hidden immediately. You can restore it within 24h from the trash, after which deletion becomes permanent.')}
                  </p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-xs text-zinc-400">
                  {L('Astuce : archivez plutôt pour masquer ce programme durablement tout en gardant vos données.', 'Tip: archive it instead to hide this program permanently while keeping your data.')}
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={runArchiveToggle} disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-white/10 hover:bg-white/15 transition-colors"
                    data-testid={`${testIdPrefix}-confirm-archive-instead`}>
                    <Archive className="w-4 h-4" /> {L('Archiver plutôt (recommandé)', 'Archive instead (recommended)')}
                  </button>
                  <button onClick={runDelete} disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-red-600 hover:bg-red-500 transition-colors disabled:opacity-60"
                    data-testid={`${testIdPrefix}-confirm-delete-btn`}>
                    <Trash2 className="w-4 h-4" /> {L('Supprimer (récupérable 24h)', 'Delete (recoverable 24h)')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default ProgramCardMenu;
